import React, { useEffect, useId, useRef, useState } from 'react';
import './MultiMetricChart.css';

const MAX_CHART_ZOOM = 10;

const MultiMetricChart = ({
    series,
    metricsConfig,
    selectedMetrics,
    formatValueWithUnit,
    formatXAxisLabel,
    formatTooltipLabel,
    onPointClick,
    onHoverPoint,
    externalHoverXValue,
    xDomainStart,
    xDomainEnd,
}) => {
    // Unique per chart instance, so two charts rendered on the same page at
    // once (however unlikely today) never clash over the same clip-path id.
    const plotClipId = `chart-plot-area-${useId()}`;
    const [selectedRange, setSelectedRange] = useState(null);
    const [isSelectionSummaryVisible, setIsSelectionSummaryVisible] = useState(false);
    const [hoveredPoint, setHoveredPoint] = useState(null);
    // Zoom/pan into the x-axis, same idea as the route map's zoom - `zoom`
    // is a multiplier on the full domain width (1 = fully zoomed out) and
    // `panOffset` is how far the visible window's left edge sits from the
    // full domain's own start, in the same units as the domain itself.
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState(0);
    const chartRef = useRef(null);
    const dragStart = useRef(null);
    const hasDragged = useRef(false);
    // Active touch contacts on the chart, and where a pinch gesture started
    // - mirrors the same tracking the route map uses so a second finger
    // turns single-finger range-selection into pinch-to-zoom instead.
    const activePointersRef = useRef(new Map());
    const pinchStartRef = useRef(null);
    // Which pointer, if any, is currently dragging the scrub bar below the
    // chart (the only place a drag pans once zoomed in - a drag on the
    // chart itself is always range-selection, same as unzoomed).
    const scrubDragRef = useRef(null);
    const scrubTrackRef = useRef(null);
    // The wheel listener below is a plain DOM listener attached once (not a
    // React onWheel prop, which Chrome attaches as passive and so can't
    // preventDefault the page's own scroll) - it reads zoom/pan/domain
    // state through this ref instead of closing over them, so it always
    // sees the latest values without needing to be re-attached every time
    // they change.
    const chartGeometryRef = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setZoom(1); setPanOffset(0); }, [xDomainStart, xDomainEnd]);

    const chartHeight = 300;
    const visibleSeries = series.filter((graphSeries) => graphSeries.points.length > 0);
    const axisGroups = visibleSeries.reduce((groups, graphSeries) => {
        const unit = metricsConfig[graphSeries.metric].unit || 'time';
        if (!groups[unit]) groups[unit] = [];
        groups[unit].push(graphSeries);
        return groups;
    }, {});
    const axes = Object.values(axisGroups).map((axisSeries) => ({
        unit: metricsConfig[axisSeries[0].metric].unit,
        metrics: axisSeries.map((graphSeries) => graphSeries.metric),
        series: axisSeries,
    }));
    const primaryMetric = visibleSeries[0]?.metric || selectedMetrics[0];
    const primaryAxisIndex = axes.findIndex((axis) => axis.metrics.includes(primaryMetric));
    const secondaryAxes = axes.filter((_, index) => index !== primaryAxisIndex);
    const secondaryAxisCount = secondaryAxes.length;
    const secondaryAxisWidth = 72;
    const chartWidth = 760 + secondaryAxisCount * secondaryAxisWidth;
    const padding = { top: 24, right: 22 + secondaryAxisCount * secondaryAxisWidth, bottom: 42, left: 68 };
    const primaryConfig = metricsConfig[primaryMetric] || Object.values(metricsConfig)[0];
    const points = visibleSeries[0]?.points || [];
    const scaleForAxis = (axis) => {
        const values = axis.series.flatMap((graphSeries) => graphSeries.points.map((point) => point.value));
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const dataRange = maxValue - minValue;
        const valuePadding = dataRange ? dataRange * 0.1 : Math.max(Math.abs(maxValue) * 0.1, 1);
        const configuredMin = Math.max(...axis.metrics.map((metric) => metricsConfig[metric].minValue ?? -Infinity));
        const configuredMax = Math.min(...axis.metrics.map((metric) => metricsConfig[metric].maxValue ?? Infinity));
        const paddedMin = axis.metrics.some((metric) => metricsConfig[metric].allowNegative)
            ? minValue - valuePadding
            : Math.max(0, minValue - valuePadding);
        const min = Math.max(paddedMin, configuredMin);
        const max = Math.min(maxValue + valuePadding, configuredMax);
        return { min, max, range: max - min || 1 };
    };
    const scalesByMetric = Object.fromEntries(axes.flatMap((axis) => axis.metrics.map((metric) => [metric, scaleForAxis(axis)])));
    const yForValue = (value, metric) => {
        const scale = scalesByMetric[metric];
        const reverse = metricsConfig[metric].reverse;
        return padding.top + (reverse ? (value - scale.min) / scale.range : 1 - (value - scale.min) / scale.range) * (chartHeight - padding.top - padding.bottom);
    };
    const primaryScale = scalesByMetric[primaryMetric] || { min: 0, max: 1, range: 1 };
    // Where zero sits on the primary axis, when the data straddles it - the
    // evenly spaced ticks below almost never land exactly on 0, so a
    // negative-going series would otherwise have nothing to read it against.
    const zeroLineY = primaryScale.min < 0 && primaryScale.max > 0 ? yForValue(0, primaryMetric) : null;
    const primaryAxisTicks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
        y: padding.top + fraction * (chartHeight - padding.top - padding.bottom),
        value: primaryConfig.reverse
            ? primaryScale.min + fraction * primaryScale.range
            : primaryScale.max - fraction * primaryScale.range,
    }));
    // The full, un-zoomed domain expressed as plain numbers either way - a
    // raw xValue for the activity-detail stream chart, or milliseconds for
    // the day/week-bucketed points ActivityGraph passes (whose xDomainStart
    // /End arrive as date strings) - so the zoom/pan state below can work
    // the same way regardless of which point shape is in play.
    const fullDomainStart = xDomainStart !== undefined && xDomainStart !== null
        ? (typeof xDomainStart === 'number' ? xDomainStart : new Date(`${xDomainStart}T00:00:00`).getTime())
        : Math.min(...points.map((point) => point.xValue ?? 0));
    const fullDomainEnd = xDomainEnd !== undefined && xDomainEnd !== null
        ? (typeof xDomainEnd === 'number' ? xDomainEnd : new Date(`${xDomainEnd}T00:00:00`).getTime())
        : Math.max(...points.map((point) => point.xValue ?? 1));
    const fullDomainRange = fullDomainEnd - fullDomainStart || 1;
    const zoomedDomainWidth = fullDomainRange / zoom;
    const maxPanOffset = Math.max(0, fullDomainRange - zoomedDomainWidth);
    const clampedPanOffset = Math.max(0, Math.min(maxPanOffset, panOffset));
    const domainStart = fullDomainStart + clampedPanOffset;
    const domainEnd = domainStart + zoomedDomainWidth;
    const domainRange = domainEnd - domainStart || 1;
    const plotWidth = chartWidth - padding.left - padding.right;
    chartGeometryRef.current = { zoom, fullDomainStart, fullDomainRange, domainStart, domainRange, chartWidth, padding };
    // Keeps whatever domain value currently sits at `anchorChartX` (chart
    // viewBox coordinates) stationary while zooming - the same "zoom to
    // where you're pointing/pinching" behaviour most map and chart UIs use,
    // rather than always re-centering on the full domain's middle.
    const zoomAroundChartX = (nextZoomRaw, anchorChartX) => {
        const nextZoom = Math.max(1, Math.min(nextZoomRaw, MAX_CHART_ZOOM));
        const anchorFraction = Math.max(0, Math.min(1, (anchorChartX - padding.left) / plotWidth));
        const anchorDomainValue = domainStart + anchorFraction * domainRange;
        const nextWidth = fullDomainRange / nextZoom;
        const nextMaxPan = Math.max(0, fullDomainRange - nextWidth);
        const nextPanOffset = Math.max(0, Math.min(nextMaxPan, (anchorDomainValue - fullDomainStart) - anchorFraction * nextWidth));
        setZoom(nextZoom);
        setPanOffset(nextPanOffset);
    };
    const zoomChart = (factor) => zoomAroundChartX(zoom * factor, padding.left + plotWidth / 2);
    const xForPoint = (point) => {
        if (point.xValue !== undefined) {
            return padding.left + ((point.xValue - domainStart) / domainRange) * plotWidth;
        }
        const pointStartTime = new Date(`${point.start}T00:00:00`).getTime();
        const pointEndTime = new Date(`${point.end}T00:00:00`).getTime();
        const visibleStartTime = Math.max(domainStart, pointStartTime);
        const visibleEndTime = Math.min(domainEnd, pointEndTime);
        const visibleMidpoint = visibleStartTime <= visibleEndTime
            ? (visibleStartTime + visibleEndTime) / 2
            : Math.max(domainStart, Math.min(domainEnd, pointStartTime));
        return padding.left + ((visibleMidpoint - domainStart) / domainRange) * plotWidth;
    };
    const pointPosition = (point) => ({
        x: xForPoint(point),
        y: yForValue(point.value, primaryMetric),
    });
    const seriesPath = (graphSeries) => graphSeries.points.map((point, index) => {
        const x = xForPoint(point);
        const y = yForValue(point.value, graphSeries.metric);
        return `${index ? 'L' : 'M'} ${x} ${y}`;
    }).join(' ');
    const validSelectedRange = selectedRange && points[selectedRange.start] && points[selectedRange.end]
        ? selectedRange
        : null;
    const selectedStartPoint = validSelectedRange ? points[validSelectedRange.start] : null;
    const selectedEndPoint = validSelectedRange ? points[validSelectedRange.end] : null;
    const isPointInSelectedRange = (point) => {
        if (!selectedStartPoint || !selectedEndPoint) return false;
        if (point.xValue !== undefined) return point.xValue >= selectedStartPoint.xValue && point.xValue <= selectedEndPoint.xValue;
        return point.start <= selectedEndPoint.end && point.end >= selectedStartPoint.start;
    };
    // Whether the selected range still overlaps what's actually visible -
    // panning/zooming away from a selection shouldn't leave its highlight
    // or popup pinned to the edge of the window, it should just disappear
    // until you scroll back to it, the same way it would if you'd never
    // hovered/selected there at all.
    const isSelectionInView = validSelectedRange && (
        selectedStartPoint.xValue !== undefined
            ? selectedEndPoint.xValue >= domainStart && selectedStartPoint.xValue <= domainEnd
            : new Date(`${selectedEndPoint.end}T00:00:00`).getTime() >= domainStart && new Date(`${selectedStartPoint.start}T00:00:00`).getTime() <= domainEnd
    );
    const selectedMetricAverages = validSelectedRange ? visibleSeries.map((graphSeries) => {
        const selectedSeriesPoints = graphSeries.points.filter(isPointInSelectedRange);
        const selectionAggregation = metricsConfig[graphSeries.metric].selectionAggregation || 'mean';
        const totalGain = selectedSeriesPoints.reduce((total, point) => total + (point.gain ?? 0), 0);
        const totalDistance = selectedSeriesPoints.reduce((total, point) => total + (point.distanceDelta ?? 0), 0);
        return {
            metric: graphSeries.metric,
            value: selectedSeriesPoints.length
                ? selectionAggregation === 'grade_percent' && totalDistance > 0
                    ? totalGain / (totalDistance / 1000) / 10
                    : selectedSeriesPoints.reduce((total, point) => total + point.value, 0) / (selectionAggregation === 'sum' ? 1 : selectedSeriesPoints.length)
                : null,
        };
    }).filter((average) => average.value !== null) : [];
    const isSelectionSummaryShown = isSelectionSummaryVisible && isSelectionInView && selectedMetricAverages.length > 0;
    const externalHoveredPoint = externalHoverXValue === null || externalHoverXValue === undefined || points.length === 0 ? null : (() => {
        const point = points.reduce((nearestPoint, currentPoint) => (
            Math.abs((currentPoint.xValue ?? 0) - externalHoverXValue) < Math.abs((nearestPoint.xValue ?? 0) - externalHoverXValue)
                ? currentPoint
                : nearestPoint
        ), points[0]);
        const position = pointPosition(point);
        return { ...point, metric: primaryMetric, x: position.x, y: position.y };
    })();
    const activeHoveredPoint = hoveredPoint || externalHoveredPoint;
    const hoveredValues = activeHoveredPoint ? visibleSeries.map((graphSeries) => {
        const point = graphSeries.points.find((seriesPoint) => (
            activeHoveredPoint.xValue !== undefined
                ? seriesPoint.xValue === activeHoveredPoint.xValue
                : seriesPoint.start === activeHoveredPoint.start
        ));
        return point ? { metric: graphSeries.metric, value: point.value } : null;
    }).filter(Boolean) : [];
    const hoverTooltip = activeHoveredPoint ? {
        label: formatTooltipLabel(activeHoveredPoint),
        values: hoveredValues,
        // Only the guide line needs an x - the read-out itself is a
        // fixed bar above the chart, so there's nothing to position.
        lineX: activeHoveredPoint.x,
    } : null;
    const updateSelectedRange = (start, end) => {
        setSelectedRange((currentRange) => (
            currentRange?.start === start && currentRange.end === end
                ? currentRange
                : { start, end }
        ));
    };
    const nearestPointIndex = (chartX) => points.reduce((nearestIndex, point, pointIndex) => (
        Math.abs(pointPosition(point).x - chartX) < Math.abs(pointPosition(points[nearestIndex]).x - chartX)
            ? pointIndex
            : nearestIndex
    ), 0);
    // Which of the full point set actually falls within the current zoom
    // window - the 6 evenly-spaced x-axis labels below are chosen from just
    // these, so zooming in doesn't leave the axis blank because all 6 of
    // the full dataset's original label picks scrolled out of view.
    const visibleWindowPoints = points.filter((point) => {
        if (point.xValue !== undefined) return point.xValue >= domainStart && point.xValue <= domainEnd;
        const pointStartTime = new Date(`${point.start}T00:00:00`).getTime();
        const pointEndTime = new Date(`${point.end}T00:00:00`).getTime();
        return pointEndTime >= domainStart && pointStartTime <= domainEnd;
    });
    const maxSeriesPointCount = Math.max(...visibleSeries.map((graphSeries) => graphSeries.points.length), 0);
    const markerStep = Math.max(1, Math.ceil(maxSeriesPointCount / 120));
    const markerRadius = maxSeriesPointCount > 180 || visibleSeries.length > 3 ? 2 : maxSeriesPointCount > 80 ? 2.6 : 3.5;
    const highlightedMarkerRadius = maxSeriesPointCount > 180 || visibleSeries.length > 3 ? 3.8 : maxSeriesPointCount > 80 ? 4.6 : 6;
    const sampleRenderedPoints = (seriesPoints) => seriesPoints.filter((_, index) => index % markerStep === 0);
    const hoveredPointFromEvent = (event) => {
        const box = chartRef.current.getBoundingClientRect();
        const chartX = ((event.clientX - box.left) / box.width) * chartWidth;
        const chartY = ((event.clientY - box.top) / box.height) * chartHeight;
        // The axis label margins (left of padding.left, right of
        // chartWidth-padding.right, below chartHeight-padding.bottom) are
        // part of the same svg but aren't the plotted area - hovering over
        // a y-axis number or an x-axis date shouldn't light up whatever
        // point happens to be nearest on the other side of that margin.
        const isInsidePlotArea = chartX >= padding.left && chartX <= chartWidth - padding.right && chartY >= padding.top && chartY <= chartHeight - padding.bottom;
        if (!isInsidePlotArea) {
            setHoveredPoint(null);
            onHoverPoint?.(null);
            return;
        }
        const index = nearestPointIndex(chartX);
        const point = points[index];
        if (point) {
            const position = pointPosition(point);
            const nextHoveredPoint = { ...point, metric: primaryMetric, x: position.x, y: position.y };
            setHoveredPoint(nextHoveredPoint);
            onHoverPoint?.(point);
        }
    };
    const setRangeFromEvent = (event, isStart) => {
        const box = chartRef.current.getBoundingClientRect();
        const chartX = ((event.clientX - box.left) / box.width) * chartWidth;
        const index = nearestPointIndex(chartX);
        if (isStart) {
            dragStart.current = index;
            hasDragged.current = false;
            setIsSelectionSummaryVisible(false);
            event.currentTarget.setPointerCapture(event.pointerId);
            updateSelectedRange(index, index);
        }
        else if (dragStart.current !== null) {
            hasDragged.current = hasDragged.current || index !== dragStart.current;
            updateSelectedRange(Math.min(dragStart.current, index), Math.max(dragStart.current, index));
        }
        hoveredPointFromEvent(event);
    };
    const completeRangeSelection = (event) => {
        if (dragStart.current === null) return;
        const box = chartRef.current.getBoundingClientRect();
        const chartX = ((event.clientX - box.left) / box.width) * chartWidth;
        const selectedIndex = nearestPointIndex(chartX);
        setRangeFromEvent(event, false);
        // This pointer was only ever captured if it's the one that started
        // the drag - after a pinch, the second finger to lift reaches here
        // too (having never been captured), so this has to be checked
        // rather than released unconditionally.
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!hasDragged.current && points[selectedIndex]) {
            setSelectedRange(null);
            onPointClick?.(points[selectedIndex]);
        } else {
            setIsSelectionSummaryVisible(true);
        }
        dragStart.current = null;
    };

    // A second finger landing turns single-finger range-selection into a
    // pinch-to-zoom instead - anchored to the midpoint between the two
    // fingers, the same "zoom to where you're touching" behaviour the
    // wheel handler below gives a mouse.
    const handleChartPointerDown = (event) => {
        activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (activePointersRef.current.size === 2) {
            dragStart.current = null;
            const [first, second] = Array.from(activePointersRef.current.values());
            pinchStartRef.current = { distance: Math.hypot(second.x - first.x, second.y - first.y), zoom };
            return;
        }
        if (activePointersRef.current.size > 2) return;
        setRangeFromEvent(event, true);
    };
    const handleChartPointerMove = (event) => {
        if (activePointersRef.current.has(event.pointerId)) {
            activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        }
        if (activePointersRef.current.size === 2 && pinchStartRef.current) {
            const [first, second] = Array.from(activePointersRef.current.values());
            const distance = Math.hypot(second.x - first.x, second.y - first.y);
            if (distance > 0 && pinchStartRef.current.distance > 0) {
                const box = chartRef.current.getBoundingClientRect();
                const anchorChartX = (((first.x + second.x) / 2 - box.left) / box.width) * chartWidth;
                zoomAroundChartX(pinchStartRef.current.zoom * (distance / pinchStartRef.current.distance), anchorChartX);
            }
            return;
        }
        if (dragStart.current !== null) {
            setRangeFromEvent(event, false);
        } else {
            hoveredPointFromEvent(event);
        }
    };
    const handleChartPointerUp = (event) => {
        activePointersRef.current.delete(event.pointerId);
        if (activePointersRef.current.size < 2) pinchStartRef.current = null;
        if (activePointersRef.current.size === 0) {
            completeRangeSelection(event);
        } else if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    // The bottom scrub bar's own drag handling - separate from the chart's
    // pointer handlers above, so it never competes with range-selection.
    // Only an actual drag moves the view - a plain click/tap doesn't jump
    // it to wherever was clicked, it tracks the pointer's movement from
    // wherever the drag started instead, like an ordinary scrollbar thumb.
    const handleScrubPointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        scrubDragRef.current = { pointerId: event.pointerId, startClientX: event.clientX, startPanOffset: clampedPanOffset };
    };
    const handleScrubPointerMove = (event) => {
        if (scrubDragRef.current?.pointerId !== event.pointerId) return;
        const track = scrubTrackRef.current;
        if (!track) return;
        const box = track.getBoundingClientRect();
        const domainDelta = ((event.clientX - scrubDragRef.current.startClientX) / box.width) * fullDomainRange;
        setPanOffset(Math.max(0, Math.min(maxPanOffset, scrubDragRef.current.startPanOffset + domainDelta)));
    };
    const handleScrubPointerUp = (event) => {
        if (scrubDragRef.current?.pointerId !== event.pointerId) return;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        scrubDragRef.current = null;
    };

    // A native (non-passive) listener, same reasoning as the route map's
    // wheel handler - React's onWheel is attached passive, so it can't
    // preventDefault the page's own scroll-zoom.
    useEffect(() => {
        const chartElement = chartRef.current;
        if (!chartElement) return undefined;
        const zoomChartFromWheel = (event) => {
            event.preventDefault();
            const geometry = chartGeometryRef.current;
            if (!geometry) return;
            const box = chartElement.getBoundingClientRect();
            const anchorChartX = ((event.clientX - box.left) / box.width) * geometry.chartWidth;
            const plotWidthNow = geometry.chartWidth - geometry.padding.left - geometry.padding.right;
            const anchorFraction = Math.max(0, Math.min(1, (anchorChartX - geometry.padding.left) / plotWidthNow));
            const anchorDomainValue = geometry.domainStart + anchorFraction * geometry.domainRange;
            const nextZoom = Math.max(1, Math.min(geometry.zoom * (event.deltaY < 0 ? 1.2 : 1 / 1.2), MAX_CHART_ZOOM));
            const nextWidth = geometry.fullDomainRange / nextZoom;
            const nextMaxPan = Math.max(0, geometry.fullDomainRange - nextWidth);
            const nextPanOffset = Math.max(0, Math.min(nextMaxPan, (anchorDomainValue - geometry.fullDomainStart) - anchorFraction * nextWidth));
            setZoom(nextZoom);
            setPanOffset(nextPanOffset);
        };
        chartElement.addEventListener('wheel', zoomChartFromWheel, { passive: false });
        // Same iOS Safari quirk as the route map: a two-finger touch fires
        // Safari's own non-standard gesture* events, which touch-action
        // doesn't reliably suppress, ahead of our own pinch handling below.
        // Harmless no-op on browsers that never fire these.
        const blockNativeGesture = (event) => event.preventDefault();
        chartElement.addEventListener('gesturestart', blockNativeGesture);
        chartElement.addEventListener('gesturechange', blockNativeGesture);
        return () => {
            chartElement.removeEventListener('wheel', zoomChartFromWheel);
            chartElement.removeEventListener('gesturestart', blockNativeGesture);
            chartElement.removeEventListener('gesturechange', blockNativeGesture);
        };
    }, []);

    return <div className="multi-metric-chart">
        <div className="multi-metric-chart-controls" aria-label="Chart zoom controls">
            <button type="button" aria-label="Zoom out" onClick={() => zoomChart(1 / 1.35)}>-</button>
            <button type="button" aria-label="Zoom in" onClick={() => zoomChart(1.35)}>+</button>
        </div>
        {/* A fixed bar directly above the plot, the chart's own width and
            left-aligned in it - not a popup chasing the pointer, which
            with several metrics selected is wider than the chart and ends
            up hanging off the side of it. The row is always here, so
            nothing shifts as it fills and empties, and the dashed guide
            line on the plot is what says which point is being read out. A
            range selection's summary takes the row over from the hover
            read-out while it's up. */}
        <div className="multi-metric-chart-readout" aria-live="polite">
            {isSelectionSummaryShown
                ? <div className="chart-readout chart-readout-selection"><small>Selected range</small>{selectedMetricAverages.map((average) => <span key={average.metric}><i style={{ background: metricsConfig[average.metric].colour }} />{metricsConfig[average.metric].label}: <strong>{formatValueWithUnit(average.value, average.metric)}</strong></span>)}</div>
                : hoverTooltip && <div className="chart-readout"><small>{hoverTooltip.label}</small>{hoverTooltip.values.map((value) => <span key={value.metric}><i style={{ background: metricsConfig[value.metric].colour }} />{metricsConfig[value.metric].label}: <strong>{formatValueWithUnit(value.value, value.metric)}</strong></span>)}</div>}
        </div>
        <svg ref={chartRef} className="activity-chart" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label={`${primaryConfig.label} chart`} onPointerDown={handleChartPointerDown} onPointerMove={handleChartPointerMove} onPointerUp={handleChartPointerUp} onPointerCancel={handleChartPointerUp} onPointerLeave={() => { setHoveredPoint(null); onHoverPoint?.(null); }}>
            <defs>
                {/* Zoomed-in data can compute to pixel positions past the
                    plot rectangle's own edges (there's more line either side
                    of what's visible) - the SVG's outer viewBox only clips
                    at the whole chart's bounds, not this inner rectangle, so
                    without this the line/points bleed into the axis label
                    margins on either side. */}
                <clipPath id={plotClipId}><rect x={padding.left} y={padding.top} width={plotWidth} height={chartHeight - padding.top - padding.bottom} /></clipPath>
            </defs>
            {zeroLineY !== null && <line x1={padding.left} x2={chartWidth - padding.right} y1={zeroLineY} y2={zeroLineY} className="chart-grid chart-baseline" />}
            {primaryAxisTicks.map((tick) => <g key={tick.y}><line x1={padding.left} x2={chartWidth - padding.right} y1={tick.y} y2={tick.y} className={`chart-grid${tick.value === 0 ? ' chart-baseline' : ''}`} /><text x="5" y={tick.y + 4}>{formatValueWithUnit(tick.value, primaryMetric)}</text></g>)}
            {secondaryAxes.map((axis, axisIndex) => { const axisMetric = axis.metrics[0]; const metricScale = scalesByMetric[axisMetric]; const axisX = chartWidth - padding.right + 10 + axisIndex * secondaryAxisWidth; return <g key={`${axis.unit || 'time'}-axis`} className="chart-secondary-axis" style={{ color: metricsConfig[axisMetric].colour }}><line x1={axisX - 4} x2={axisX - 4} y1={padding.top} y2={chartHeight - padding.bottom} />{[0, 0.25, 0.5, 0.75, 1].map((fraction) => { const y = padding.top + fraction * (chartHeight - padding.top - padding.bottom); const value = metricsConfig[axisMetric].reverse ? metricScale.min + fraction * metricScale.range : metricScale.max - fraction * metricScale.range; return <g key={fraction}><line x1={axisX - 4} x2={axisX} y1={y} y2={y} /><text x={axisX + 2} y={y + 3}>{formatValueWithUnit(value, axisMetric)}</text></g>; })}</g>; })}
            <g clipPath={`url(#${plotClipId})`}>
                {/* With the read-out itself now above the chart, this is
                    what ties it to a point on the plot. */}
                {hoverTooltip && <line className="chart-hover-line" x1={hoverTooltip.lineX} x2={hoverTooltip.lineX} y1={padding.top} y2={chartHeight - padding.bottom} />}
                {isSelectionInView && <rect className="chart-selection" x={pointPosition(selectedStartPoint).x} y={padding.top} width={pointPosition(selectedEndPoint).x - pointPosition(selectedStartPoint).x} height={chartHeight - padding.top - padding.bottom} />}
                {visibleSeries.map((graphSeries) => <path key={graphSeries.metric} d={seriesPath(graphSeries)} fill="none" stroke={metricsConfig[graphSeries.metric].colour} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />)}
                {visibleSeries.map((graphSeries) => sampleRenderedPoints(graphSeries.points).map((point, index) => { const y = yForValue(point.value, graphSeries.metric); const isHighlighted = isPointInSelectedRange(point); return <circle key={`${graphSeries.metric}-${point.xValue ?? point.start}-${index}`} className={`chart-series-point${isHighlighted ? ' chart-series-point-highlighted' : ''}`} cx={xForPoint(point)} cy={y} r={isHighlighted ? highlightedMarkerRadius : markerRadius} fill={metricsConfig[graphSeries.metric].colour} />; }))}
            </g>
            {visibleWindowPoints.filter((_, index) => index % Math.max(1, Math.ceil(visibleWindowPoints.length / 6)) === 0).map((point, index) => { const position = pointPosition(point); return <text key={`${point.xValue ?? point.start}-${index}`} x={position.x} y={chartHeight - 12} textAnchor="middle">{formatXAxisLabel(point, index)}</text>; })}
       </svg>
        {zoom > 1 && <div className="multi-metric-chart-scrub" aria-label="Drag to scroll the zoomed chart">
            <div ref={scrubTrackRef} className="multi-metric-chart-scrub-track" onPointerDown={handleScrubPointerDown} onPointerMove={handleScrubPointerMove} onPointerUp={handleScrubPointerUp} onPointerCancel={handleScrubPointerUp}>
                <div className="multi-metric-chart-scrub-thumb" style={{ left: `${(clampedPanOffset / fullDomainRange) * 100}%`, width: `${(zoomedDomainWidth / fullDomainRange) * 100}%` }} />
            </div>
        </div>}
    </div>;
};

export default MultiMetricChart;